import { useEffect, useState } from "react";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";

import { type AdminSession, clearSession, isSessionExpired, loadSession, saveSession } from "./api";
import { Layout } from "./components/Layout";
import { AuditExplorer } from "./pages/AuditExplorer";
import { BackupRecovery } from "./pages/BackupRecovery";
import { BreakGlass } from "./pages/BreakGlass";
import { Cost } from "./pages/Cost";
import { Infrastructure } from "./pages/Infrastructure";
import { Keys } from "./pages/Keys";
import { Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { Projects } from "./pages/Projects";
import { Ransomware } from "./pages/Ransomware";
import { SecurityCenter } from "./pages/SecurityCenter";
import { Storage } from "./pages/Storage";
import { StoragePolicies } from "./pages/StoragePolicies";
import { Users } from "./pages/Users";

function initialSession(): AdminSession | null {
  const session = loadSession();
  if (session && isSessionExpired(session)) {
    clearSession();
    return null;
  }
  return session;
}

export default function App(): JSX.Element {
  const [session, setSession] = useState<AdminSession | null>(initialSession);

  // The API client (`api.ts request()`) reads the token from `sessionStorage`
  // on every call, NOT from this React state — so a fresh login MUST persist
  // the session there, or every request goes out with no
  // `X-Admin-Session-Token` and the backend answers "Missing ... header" on
  // every page. Sign-out must clear it symmetrically.
  function handleSignedIn(next: AdminSession): void {
    saveSession(next);
    setSession(next);
  }

  function handleSignOut(): void {
    clearSession();
    setSession(null);
  }

  // भाग E4: "auto-logout on expiry" — a 1-hour session has to end itself
  // even if the operator never triggers another API call that would
  // otherwise notice the 401 from `admin_api.auth`.
  useEffect(() => {
    if (!session) return;
    const interval = window.setInterval(() => {
      if (isSessionExpired(session)) {
        clearSession();
        setSession(null);
      }
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [session]);

  return (
    <HashRouter>
      <Routes>
        <Route
          path="/login"
          element={session ? <Navigate to="/" replace /> : <Login onSignedIn={handleSignedIn} />}
        />
        <Route
          element={
            session ? (
              <Layout session={session} onSignOut={handleSignOut} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route path="/" element={<Overview />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/storage" element={<Storage />} />
          <Route path="/storage-policies" element={<StoragePolicies />} />
          <Route path="/users" element={<Users />} />
          <Route path="/security-center" element={<SecurityCenter />} />
          <Route path="/ransomware" element={<Ransomware />} />
          <Route path="/backup-recovery" element={<BackupRecovery />} />
          <Route path="/infrastructure" element={<Infrastructure />} />
          <Route path="/cost" element={<Cost />} />
          <Route path="/keys" element={<Keys />} />
          <Route path="/audit" element={<AuditExplorer />} />
          <Route path="/break-glass" element={<BreakGlass />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}
